import { isWithinPendingWindow } from "./metricPending";

export const RECORDING_QUALITY_CHECK_STATES = {
  PASS: "pass",
  FAIL: "fail",
  PENDING: "pending",
  UNAVAILABLE: "unavailable",
};

export const RECORDING_QUALITY_OVERALL_STATES = {
  PASSED: "passed",
  REPEAT: "repeat",
  PENDING: "pending",
  UNAVAILABLE: "unavailable",
};

const PASS_VALUES = new Set([
  "pass",
  "passed",
  "good",
  "very good",
  "very_good",
  "verygood",
  "ok",
]);

const FAIL_VALUES = new Set([
  "fail",
  "failed",
  "bad",
  "poor",
  "low",
]);

const PENDING_VALUES = new Set([
  "pending",
  "processing",
  "in progress",
  "in_progress",
  "calculating",
]);

function normalizeString(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function translateCount(t, key, count, one, other) {
  return t(key, {
    count,
    defaultValue: count === 1 ? one : other,
  });
}

export function normalizeRecordingQualityCheckState(value) {
  const normalized = normalizeString(value);

  if (!normalized) {
    return null;
  }

  if (PASS_VALUES.has(normalized)) {
    return RECORDING_QUALITY_CHECK_STATES.PASS;
  }

  if (FAIL_VALUES.has(normalized) || normalized.startsWith("fail")) {
    return RECORDING_QUALITY_CHECK_STATES.FAIL;
  }

  if (PENDING_VALUES.has(normalized)) {
    return RECORDING_QUALITY_CHECK_STATES.PENDING;
  }

  return null;
}

export function buildRecordingQualityCheck({
  key,
  label,
  rawStatus = null,
  state = null,
  recordingTimestamp = null,
  forcePending = false,
  message = null,
}) {
  const normalizedState =
    state || normalizeRecordingQualityCheckState(rawStatus) || null;
  const resolvedState =
    normalizedState ||
    (forcePending || isWithinPendingWindow(recordingTimestamp)
      ? RECORDING_QUALITY_CHECK_STATES.PENDING
      : RECORDING_QUALITY_CHECK_STATES.UNAVAILABLE);

  return {
    key,
    label,
    message,
    rawStatus,
    state: resolvedState,
  };
}

export function summarizeRecordingQualityChecks(checks = []) {
  const visibleChecks = checks.filter(Boolean);
  const failedChecks = visibleChecks.filter(
    (check) => check.state === RECORDING_QUALITY_CHECK_STATES.FAIL,
  );
  const pendingChecks = visibleChecks.filter(
    (check) => check.state === RECORDING_QUALITY_CHECK_STATES.PENDING,
  );
  const passedChecks = visibleChecks.filter(
    (check) => check.state === RECORDING_QUALITY_CHECK_STATES.PASS,
  );
  const unavailableChecks = visibleChecks.filter(
    (check) => check.state === RECORDING_QUALITY_CHECK_STATES.UNAVAILABLE,
  );
  const availableCheckCount =
    failedChecks.length + pendingChecks.length + passedChecks.length;

  let overallState = RECORDING_QUALITY_OVERALL_STATES.UNAVAILABLE;

  if (failedChecks.length > 0) {
    overallState = RECORDING_QUALITY_OVERALL_STATES.REPEAT;
  } else if (pendingChecks.length > 0) {
    overallState = RECORDING_QUALITY_OVERALL_STATES.PENDING;
  } else if (availableCheckCount > 0 && unavailableChecks.length === 0) {
    overallState = RECORDING_QUALITY_OVERALL_STATES.PASSED;
  }

  return {
    overallState,
    checks: visibleChecks,
    applicableCheckCount: visibleChecks.length,
    availableCheckCount,
    failedChecks,
    pendingChecks,
    passedChecks,
    unavailableChecks,
    sortedChecks: [
      ...failedChecks,
      ...pendingChecks,
      ...passedChecks,
      ...unavailableChecks,
    ],
  };
}

export function getRecordingQualityStatusLabel(t, state) {
  if (state === RECORDING_QUALITY_CHECK_STATES.PASS) {
    return t("result.recordingQuality.status.passed", "Passed");
  }

  if (state === RECORDING_QUALITY_CHECK_STATES.FAIL) {
    return t("result.recordingQuality.status.failed", "Failed");
  }

  if (state === RECORDING_QUALITY_CHECK_STATES.PENDING) {
    return t("result.recordingQuality.status.pending", "In progress");
  }

  return "--";
}

export function getRecordingQualityHealthNotice(t) {
  return t(
    "result.recordingQuality.healthNotice",
    "Quality checks assess the recording, not your health.",
  );
}

export function getRecordingQualitySummaryCopy(
  t,
  summary,
  { surface = "detail" } = {},
) {
  if (!summary) {
    return null;
  }

  if (summary.overallState === RECORDING_QUALITY_OVERALL_STATES.PASSED) {
    const count = summary.availableCheckCount;
    return {
      tone: RECORDING_QUALITY_OVERALL_STATES.PASSED,
      title: translateCount(
        t,
        "result.recordingQuality.summary.passed",
        count,
        "Quality check passed",
        "Quality checks passed",
      ),
      body: null,
      accessibleLabel: translateCount(
        t,
        "result.recordingQuality.accessibility.passed",
        count,
        "Recording quality check passed.",
        "Recording quality checks passed.",
      ),
    };
  }

  if (summary.overallState === RECORDING_QUALITY_OVERALL_STATES.PENDING) {
    const count = summary.pendingChecks.length;
    return {
      tone: RECORDING_QUALITY_OVERALL_STATES.PENDING,
      title: translateCount(
        t,
        "result.recordingQuality.summary.pending",
        count,
        "Quality check in progress",
        "Quality checks in progress",
      ),
      body: null,
      accessibleLabel: translateCount(
        t,
        "result.recordingQuality.accessibility.pending",
        count,
        "Recording quality check in progress.",
        "Recording quality checks in progress.",
      ),
    };
  }

  if (summary.overallState === RECORDING_QUALITY_OVERALL_STATES.REPEAT) {
    const count = summary.failedChecks.length;

    if (surface === "history") {
      return {
        tone: RECORDING_QUALITY_OVERALL_STATES.REPEAT,
        title: t("result.recordingQuality.summary.repeatCardTitle", "Please repeat"),
        body: translateCount(
          t,
          "result.recordingQuality.summary.repeatCardBody",
          count,
          "Quality check failed",
          "Quality checks failed",
        ),
        accessibleLabel: translateCount(
          t,
          "result.recordingQuality.accessibility.repeatHistory",
          count,
          "Please repeat. Quality check failed.",
          "Please repeat. Quality checks failed.",
        ),
      };
    }

    return {
      tone: RECORDING_QUALITY_OVERALL_STATES.REPEAT,
      title: t(
        "result.recordingQuality.summary.repeatDetailTitle",
        "Please repeat this measurement",
      ),
      body: t(
        "result.recordingQuality.summary.repeatDetailBody",
        "We couldn't get a reliable reading this time.",
      ),
      accessibleLabel: translateCount(
        t,
        "result.recordingQuality.accessibility.repeatDetail",
        count,
        "Please repeat this measurement. We couldn't get a reliable reading this time.",
        "Please repeat this measurement. We couldn't get a reliable reading this time.",
      ),
    };
  }

  return null;
}
